import React, { createContext, FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import EnterpriseComplianceDashboardService from "../enterpriseComplianceDashboardService";
import {
    AdequacyBreakdownData,
    TopCountiesByGapData,
    ExceptionsData,
    TopSpecialtiesByGapData,
    CompositeFillableGapsData,
    LineOfBusinessData,
    CountyStatusData,
    GapOverviewData,
    GapsByAgeData,
    EcdTilesEnum,
    EcdWidgetData,
    GapCountDrillInModel,
    EcdApiFilters,
    CompositeFillableGapCountDrillInModel,
    ExceptionsDrillInModel,
    AdequacyBreakdownDrillInModel,
} from "../interfaces";
import { EnterpriseComplianceFiltersContext } from "./EcdFiltersContext";

export interface EnterpriseComplianceDashboardContextValues {
    getWidgetData: () => void;
    getAdequacyBreakdownDrillInData: () => Promise<AdequacyBreakdownDrillInModel[]>;
    getTopCountiesByGapData: () => void;
    getTopCountiesByGapDrillInData: (modifiedFilters: EcdApiFilters) => Promise<GapCountDrillInModel[]>;
    getExceptionsData: () => void;
    getExceptionsDrillInData: () => Promise<ExceptionsDrillInModel[]>;
    getCompositeFillableGapsData: () => void;
    getCompositeFillableGapsDrillInData: () => Promise<CompositeFillableGapCountDrillInModel[]>;
    getGapsBySpecialtyData: () => void;
    getGapsBySpecialtyDrillInData: (modifiedFilters: EcdApiFilters) => Promise<GapCountDrillInModel[]>;

    adequacyBreakdownData: AdequacyBreakdownData;
    topCountiesByGapData: TopCountiesByGapData[];
    countyStatusData: CountyStatusData[];
    exceptionsData: ExceptionsData[];
    compositeFillableGapsData: CompositeFillableGapsData;
    gapOverviewData: GapOverviewData[];
    gapsByAgeData: GapsByAgeData[];
    gapsBySpecialtyData: TopSpecialtiesByGapData[];
    isTileLoadingMap: Map<EcdTilesEnum, boolean>;
    lineOfBusinessData: LineOfBusinessData[];

    showNoResultsAlert: boolean;
    updateIsTileLoadingMap: (key: EcdTilesEnum, isLoading: boolean) => void;
    isDataEmptyMap: Map<EcdTilesEnum, boolean>;

    showExportButton: boolean;
}

const initialValues: EnterpriseComplianceDashboardContextValues = {
    adequacyBreakdownData: null,
    compositeFillableGapsData: null,
    countyStatusData: [],
    exceptionsData: [],
    gapOverviewData: [],
    gapsByAgeData: [],
    gapsBySpecialtyData: [],
    getAdequacyBreakdownDrillInData: () => null,
    getCompositeFillableGapsData: () => null,
    getCompositeFillableGapsDrillInData: () => null,
    getExceptionsData: () => null,
    getExceptionsDrillInData: () => null,
    getGapsBySpecialtyData: () => null,
    getGapsBySpecialtyDrillInData: () => null,
    getTopCountiesByGapData: () => null,
    getTopCountiesByGapDrillInData: () => null,
    getWidgetData: () => null,
    isDataEmptyMap: null,
    isTileLoadingMap: new Map<EcdTilesEnum, boolean>(),
    lineOfBusinessData: [],
    showExportButton: false,
    showNoResultsAlert: false,
    topCountiesByGapData: [],
    updateIsTileLoadingMap: () => null,
}

export const EnterpriseComplianceDashboardContext = createContext(initialValues);

const EnterpriseComplianceDashboardContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
    const filtersContext = useContext(EnterpriseComplianceFiltersContext);

    const [adequacyBreakdownData, setAdequacyBreakdownData] = useState<AdequacyBreakdownData>(initialValues.adequacyBreakdownData);
    const [topCountiesByGapData, setTopCountiesByGapData] = useState<TopCountiesByGapData[]>(initialValues.topCountiesByGapData);
    const [exceptionsData, setExceptionsData] = useState<ExceptionsData[]>(initialValues.exceptionsData);
    const [gapOverviewData, setGapOverviewData] = useState<GapOverviewData[]>(initialValues.gapOverviewData);
    const [compositeFillableGapsData, setCompositeFillableGapsData] = useState<CompositeFillableGapsData>(initialValues.compositeFillableGapsData);
    const [gapsByAgeData, setGapsByAgeData] = useState<GapsByAgeData[]>(initialValues.gapsByAgeData);
    const [gapsBySpecialtyData, setGapsBySpecialtyData] = useState<TopSpecialtiesByGapData[]>(initialValues.gapsBySpecialtyData);
    const [lineOfBusinessData, setLineOfBusinessData] = useState<LineOfBusinessData[]>(initialValues.lineOfBusinessData);
    const [countyStatusData, setCountyStatusData] = useState<CountyStatusData[]>(initialValues.countyStatusData);
    // if, at some point, we let clients show/hide tiles, isDataEmptyMap.delete(key) on a removed tile will work
    const [isDataEmptyMap, setIsDataEmptyMap] = useState(new Map<EcdTilesEnum, boolean>());
    const [isTileLoadingMap, setIsTileLoadingMap] = useState(initialValues.isTileLoadingMap);
    const [showExportButton, setShowExportButton] = useState(false);

    const enterpriseComplianceDashboardService = useRef(new EnterpriseComplianceDashboardService()).current;

    useEffect(() => {
        if (filtersContext.isDashboardLoading && (isTileLoadingMap.size > 0 && [...isTileLoadingMap.values()].some((x) => x === false))) {
            filtersContext.setIsDashboardLoading(false);
        }
        const showButton = Array.from(isTileLoadingMap.values()).every(mapValue => mapValue === false);
        setShowExportButton(showButton);
    }, [isTileLoadingMap]);

    /**
     * Retrieve the data for all the widgets.
     */
    useEffect(() => {
        if (!!filtersContext.apiReadyFilters) {
            getWidgetData();
            getTopCountiesByGapData();
            getExceptionsData();
            getCompositeFillableGapsData();
            getGapsBySpecialtyData();
        }
    }, [filtersContext.apiReadyFilters]);

    const showNoResultsAlert = useMemo(() => {
        return isDataEmptyMap.size > 0 && [...isDataEmptyMap.values()].every((x) => x === true);
    }, [isDataEmptyMap]);

    const updateIsDataEmptyMap = (key: EcdTilesEnum, isEmpty: boolean) => {
        setIsDataEmptyMap((oldMap) => {
            const newMap = new Map(oldMap);
            newMap.set(key, isEmpty);
            return newMap;
        });
    }

    const updateIsTileLoadingMap = (key: EcdTilesEnum, isLoading: boolean) => {
        setIsTileLoadingMap((oldMap) => {
            const newMap = new Map(oldMap);
            newMap.set(key, isLoading);
            return newMap;
        });
    }

    const getWidgetData = async () => {
        const isEmpty: { [key: string]: boolean } = {
            [EcdTilesEnum.AdequacyBreakdown]: false,
            [EcdTilesEnum.CountyStatusOverview]: false,
            [EcdTilesEnum.GapsByAge]: false,
            [EcdTilesEnum.GapOverview]: false,
            [EcdTilesEnum.LineOfBusinessOverview]: false,
        };

        if (filtersContext.forceNoResults) {
            const data: EcdWidgetData = await enterpriseComplianceDashboardService.getEmptyWidgetData();
            setAdequacyBreakdownData(data.adequacyBreakdownDataModel);
            setCountyStatusData(data.countyStatusOverviewModels);
            setGapsByAgeData(data.gapCountByAgeModels);
            setGapOverviewData(data.gapOverviewDataModels);
            setLineOfBusinessData(data.networkTaxonomyLineOfBusinessOverviewModels);
            Object.keys(isEmpty).forEach((key) => {
                isEmpty[key] = true
            });
        } else {
            const data = await enterpriseComplianceDashboardService.getWidgetData(filtersContext.apiReadyFilters);
            if (!!data) {
                if (!!data.adequacyBreakdownDataModel &&
                    (parseInt(data.adequacyBreakdownDataModel.countySpecialtyGroupCount, 10) > 0 ||
                        parseInt(data.adequacyBreakdownDataModel.countySpecialtyGroupCountPrior, 10) > 0)
                ) {
                    setAdequacyBreakdownData(data.adequacyBreakdownDataModel);
                } else {
                    enterpriseComplianceDashboardService.getEmptyAdequacyBreakdownData().then((data) => { setAdequacyBreakdownData(data) });
                    isEmpty[EcdTilesEnum.AdequacyBreakdown] = true;
                }

                if (data?.countyStatusOverviewModels?.length > 1 && Object.values(data.countyStatusOverviewModels[0]).some((v) => v > 0)) {
                    data.countyStatusOverviewModels.find((d) => d?.countyStatus === undefined).countyStatus = "Total";
                    setCountyStatusData(data.countyStatusOverviewModels);
                } else {
                    enterpriseComplianceDashboardService.getEmptyCountyStatusData().then((data) => { setCountyStatusData(data) });
                    isEmpty[EcdTilesEnum.CountyStatusOverview] = true;
                }

                setGapsByAgeData(data.gapCountByAgeModels);
                isEmpty[EcdTilesEnum.GapsByAge] = isGapsByAgeEmpty(data.gapCountByAgeModels);

                setGapOverviewData(data.gapOverviewDataModels);
                isEmpty[EcdTilesEnum.GapOverview] = data.gapOverviewDataModels.every((d) => d.gapCount === 0);

                if (data?.networkTaxonomyLineOfBusinessOverviewModels?.length > 1 && Object.values(data.networkTaxonomyLineOfBusinessOverviewModels[0]).some((v) => v > 0)) {
                    data.networkTaxonomyLineOfBusinessOverviewModels.find((d) => d.networkTaxonomyLOB === undefined).networkTaxonomyLOB = "Total";
                    setLineOfBusinessData(data.networkTaxonomyLineOfBusinessOverviewModels);
                } else {
                    enterpriseComplianceDashboardService.getEmptyLineOfBusinessData().then((data) => { setLineOfBusinessData(data) });
                    isEmpty[EcdTilesEnum.LineOfBusinessOverview] = true;
                }
            }
        }

        Object.entries(isEmpty).forEach(([key, value]) => {
            updateIsDataEmptyMap(key as unknown as EcdTilesEnum, value);
        });
    };

    const getAdequacyBreakdownDrillInData = async () => {
        return enterpriseComplianceDashboardService.getAdequacyBreakdownDrillInData(filtersContext.apiReadyFilters);
    }

    const getTopCountiesByGapData = async () => {
        let isEmpty = false;
        if (filtersContext.forceNoResults) {
            enterpriseComplianceDashboardService.getEmptyTopCountiesByGapData().then((data) => { setTopCountiesByGapData(data) });
            isEmpty = true;
        } else {
            const data = await enterpriseComplianceDashboardService.getTopCountiesByGapData(filtersContext.apiReadyFilters);
            if (data?.length > 0) {
                setTopCountiesByGapData(data)
            } else {
                enterpriseComplianceDashboardService.getEmptyTopCountiesByGapData().then((data) => { setTopCountiesByGapData(data) });
                isEmpty = true;
            }
        }
        updateIsDataEmptyMap(EcdTilesEnum.TopCountiesByGap, isEmpty);
    };

    const getTopCountiesByGapDrillInData = async (modifiedFilters: EcdApiFilters) => {
        return await enterpriseComplianceDashboardService.getTopCountiesByGapDrillInData(modifiedFilters);
    }

    const getExceptionsData = async () => {
        let isEmpty = false;
        if (filtersContext.forceNoResults) {
            enterpriseComplianceDashboardService.getEmptyExceptionsData().then((data) => {
                setExceptionsData(data);
            });
            isEmpty = true;
        }
        else {
            const data = await enterpriseComplianceDashboardService.getExceptionsData(filtersContext.apiReadyFilters);
            setExceptionsData(data);
            isEmpty = isExceptionsEmpty(data);
        }
        updateIsDataEmptyMap(EcdTilesEnum.Exceptions, isEmpty);
    };

    const getExceptionsDrillInData = async () => {
        return await enterpriseComplianceDashboardService.getExceptionsDrillInData(filtersContext.apiReadyFilters);
    }

    const getCompositeFillableGapsData = async () => {
        let isEmpty = false;
        if (filtersContext.forceNoResults) {
            enterpriseComplianceDashboardService.getEmptyCompositeFillableGapsData().then((data) => { setCompositeFillableGapsData(data) });
            isEmpty = true;
        }
        else {
            const data = await enterpriseComplianceDashboardService.getCompositeFillableGapsData(filtersContext.apiReadyFilters)
            setCompositeFillableGapsData(data);
            isEmpty = Object.values(data).every((d) => d === 0);
        }
        updateIsDataEmptyMap(EcdTilesEnum.CompositeFillableGaps, isEmpty);
    };

    const getCompositeFillableGapsDrillInData = async () => {
        return await enterpriseComplianceDashboardService.getCompositeFillableGapsDrillInData(filtersContext.apiReadyFilters);
    }

    const getGapsBySpecialtyData = async () => {
        let isEmpty = false;
        if (filtersContext.forceNoResults) {
            enterpriseComplianceDashboardService.getEmptyGapsBySpecialtyData().then((data) => { setGapsBySpecialtyData(data) });
            isEmpty = true;
        } else {
            const data = await enterpriseComplianceDashboardService.getGapsBySpecialtyData(filtersContext.apiReadyFilters);
            if (data.length > 0 && !!data[0]) {
                setGapsBySpecialtyData(data);
            } else {
                enterpriseComplianceDashboardService.getEmptyGapsBySpecialtyData().then((data) => setGapsBySpecialtyData(data));
                isEmpty = true;
            }
        }
        updateIsDataEmptyMap(EcdTilesEnum.TopSpecialtiesByGap, isEmpty);
    };

    const getGapsBySpecialtyDrillInData = async (modifiedFilters: EcdApiFilters) => {
        return await enterpriseComplianceDashboardService.getSpecialtiesByGapDrillInData(modifiedFilters);
    }

    const isGapsByAgeEmpty = (data: GapsByAgeData[]) => {
        return data?.every((d) => (
            d.gapAge30 === 0 &&
            d.gapAge60 === 0 &&
            d.gapAge90 === 0 &&
            d.gapAgeOver90 === 0
        ));
    }

    const isExceptionsEmpty = (data: ExceptionsData[]) => {
        return data.every((d) => (
            d.exceptionsAppealed === 0 &&
            d.exceptionsApproved === 0 &&
            d.exceptionsDenied === 0 &&
            d.exceptionsPending === 0 &&
            d.exceptionsSubmitted === 0
        ));
    }

    return (
        <EnterpriseComplianceDashboardContext.Provider
            value={{
                adequacyBreakdownData,
                compositeFillableGapsData,
                countyStatusData,
                exceptionsData,
                gapOverviewData,
                gapsByAgeData,
                gapsBySpecialtyData,
                getAdequacyBreakdownDrillInData,
                getCompositeFillableGapsData,
                getCompositeFillableGapsDrillInData,
                getExceptionsData,
                getExceptionsDrillInData,
                getGapsBySpecialtyData,
                getGapsBySpecialtyDrillInData,
                getTopCountiesByGapData,
                getTopCountiesByGapDrillInData,
                getWidgetData,
                isDataEmptyMap,
                isTileLoadingMap,
                lineOfBusinessData,
                showExportButton,
                showNoResultsAlert,
                topCountiesByGapData,
                updateIsTileLoadingMap,
            }}>
            {props.children}
        </EnterpriseComplianceDashboardContext.Provider>
    )
}

export default EnterpriseComplianceDashboardContextProvider;
